import { SortFacet } from "../../server/asin-central/search";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/rootReducer";
import { Button, ButtonDropdown, Icon } from "@amzn/awsui-components-react";
import { setEditing, setUpdatedEntries, setViewing } from "./porAsinListSlice";
import { ExportButton } from "../porExport/porExport";
import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../server/asin-central/model";
import {
  fetchValidateAsinMarketplaces,
  setEditingValidationTable,
  setViewingValidationTable,
  validateAsinMarketplacesFailure,
  validateAsinMarketplacesStart,
  validateAsinMarketplacesSuccess,
} from "../validation/asinValidationTableConnector";
import { searchPorAsinsNoLimit } from "../app/api";
import {
  clearNotifications,
  createNotificationDefinition,
  addNotification,
  setNotification,
} from "../notification/notificationConnector";
import { ERROR_NOTIF_ITEMS } from "../notification/notification-config";
import {
  getIValidationServiceById,
  VALIDATION_SERVICES,
} from "../validation/validationAttribute";
import logger from "../../art-logger-config";
import { trackButtonClick } from "../telemetry/telemetry";
import React from "react";
import { FetchBuyBoxButton } from "../porFetchBuyBox/porFetchBuyBox";
import "../../styles/tableCommonElements.css";
import { parseQuery } from "../../from-server/asin-central/search";

export const defaultSort: SortFacet[] = [
  {
    attributeName: "country",
    ascending: true,
  },
  {
    attributeName: "asinParty",
    ascending: true,
  },
];

export const TableHeaderMessage = (
  offset: number,
  resultCount: number,
  totalCount: number
): string => {
  if (resultCount > 0)
    return `Displaying ${offset + 1} - ${
      offset + resultCount
    } of ${totalCount}`;
  return `Displaying ${resultCount} of ${totalCount}`;
};

function isButtonDisabled(
  asins: (FullAsinWithAudits | RestrictedAsin)[],
  isLoading: boolean
) {
  return (
    asins?.every((item) => item.isRestricted) || asins?.length < 1 || isLoading
  );
}

interface ValidationActionButtonProps {
  totalCount: number;
}

const isValidationDisabled = (serviceName: string, totalCount: number) => {
  if (serviceName === "Hattrick" && totalCount > 150) {
    return {
      disabled: true,
      disabledReason: `Please reduce the number of ASINs using filters to less than 150 ASIN/MP combinations`,
    };
  }
  return { disabled: false, disabledReason: "" };
};

const ValidationActionButton = ({
  totalCount,
}: ValidationActionButtonProps) => {
  const dispatch = useDispatch();
  const { currentPagePorAsins, isLoading } = useSelector(
    (state: RootState) => state.porAsinList
  );
  const { queryString, queryMode } = useSelector(
    (state: RootState) => state.generateQuery
  );
  const { validationInProgress } = useSelector(
    (state: RootState) => state.asinValidationTableControl
  );

  const items = [
    {
      text: "Automated",
      items: VALIDATION_SERVICES.filter((service) => !service.isManual).map(
        (service) => {
          const { disabled, disabledReason } = isValidationDisabled(
            service.serviceName,
            totalCount
          );
          return {
            text: service.serviceName,
            id: service.serviceId,
            disabled,
            disabledReason,
          };
        }
      ),
    },
    {
      text: "Manual",
      items: VALIDATION_SERVICES.filter((service) => service.isManual).map(
        (service) => ({ text: service.serviceName, id: service.serviceId })
      ),
    },
  ];

  return (
    <ButtonDropdown
      items={items}
      onItemClick={(event) => {
        const validationService = getIValidationServiceById(event.detail.id);
        trackButtonClick("validationTable", "runValidation", {
          service: validationService,
        });
        if (validationService === null) {
          logger.error(
            `Validation Button Error: validationService was null! event: ${JSON.stringify(
              event
            )}`
          );
          return;
        }
        if (validationService.isManual) {
          dispatch(
            setEditingValidationTable({
              currentPagePorAsins,
              validationService,
            })
          );
        } else {
          dispatch(validateAsinMarketplacesStart());
          dispatch(
            createNotificationDefinition(
              "info",
              "Validation Pending",
              true,
              `Validations will begin soon...`
            )
          );
          try {
            parseQuery(queryString);
            const fullQueryString = queryString + "(queryMode)=" + queryMode;
            searchPorAsinsNoLimit(queryString)
              .then(
                (
                  allAsinsFromSearchQuery: (
                    | FullAsinWithAudits
                    | RestrictedAsin
                  )[]
                ) => {
                  dispatch(
                    fetchValidateAsinMarketplaces(
                      allAsinsFromSearchQuery,
                      validationService,
                      fullQueryString
                    )
                  );
                }
              )
              .catch((err: any) => {
                dispatch(validateAsinMarketplacesFailure(err));
                dispatch(
                  setNotification(
                    ERROR_NOTIF_ITEMS(
                      "Failed to validate asins: " + err.toString()
                    )
                  )
                );
              });
          } catch (err) {
            dispatch(
              setNotification(
                "Validation Error",
                true,
                `Invalid query format: ${err}`
              )
            );
          }
        }
      }}
      loading={validationInProgress}
      disabled={isButtonDisabled(currentPagePorAsins, isLoading)}
      variant="primary"
    >
      Run Validation
    </ButtonDropdown>
  );
};

export const PorEditButton = () => {
  const dispatch = useDispatch();
  const { currentPagePorAsins, isLoading } = useSelector(
    (state: RootState) => state.porAsinList
  );
  return (
    <div>
      <Button
        variant="primary"
        children="Edit"
        disabled={isButtonDisabled(currentPagePorAsins, isLoading)}
        onClick={(event) => {
          trackButtonClick("asinCentralTable", "edit", {});
          dispatch(setEditing());
        }}
      />
    </div>
  );
};

export const EditActionButtons = () => {
  const dispatch = useDispatch();
  const { edited, editablePorAsins, editedRowSet, currentPagePorAsins } =
    useSelector((state: RootState) => state.porAsinList);
  return (
    <div className="button-container">
      <Button
        children="Cancel"
        onClick={(event) => {
          trackButtonClick("asinCentralTable", "cancel", {});
          dispatch(setViewing());
        }}
      />
      <Button
        children="Save"
        variant="primary"
        disabled={!edited}
        onClick={(e) => {
          trackButtonClick("asinCentralTable", "save", {
            updatedAsinCount: Object.keys(editedRowSet).length,
          });
          dispatch(
            setUpdatedEntries(
              currentPagePorAsins,
              editablePorAsins,
              editedRowSet
            )
          );
          dispatch(setViewing());
        }}
      />
    </div>
  );
};

export const EmptyTableState = (
  title: string = "No Matching ASINs Found",
  message?: string
) => {
  return (
    <div className="awsui-util-t-c">
      <div className="awsui-util-pt-s awsui-util-mb-xs">
        <b>{title}</b>
        {message ? <p>{message}</p> : ""}
      </div>
    </div>
  );
};

export const EmptyValidationTableState = () => {
  return (
    <div className="awsui-util-t-c">
      <div className="awsui-util-pt-s awsui-util-mb-xs">
        <b>No Step Details</b>
      </div>
    </div>
  );
};

export const ViewActionButtons = () => {
  return (
    <div className="button-container">
      <FetchBuyBoxButton />
      <div>
        <Button
          variant={"normal"}
          onClick={(event) => {
            trackButtonClick("stamper", "stamper", {});
            window.location.href = "/stamper";
          }}
        >
          Stamper Tool
        </Button>
      </div>
      <ExportButton componentName={"asinCentralTable"} />
      <PorEditButton />
    </div>
  );
};

interface ValidationActionButtonsProps {
  totalCount: number;
}

export const ValidationActionButtons = ({
  totalCount,
}: ValidationActionButtonsProps) => {
  return (
    <div className="button-container">
      <ExportButton componentName={"validationTable"} />
      <ValidationActionButton totalCount={totalCount} />
    </div>
  );
};

export const ValidationEditButtons = () => {
  const dispatch = useDispatch();
  const {
    edited,
    editableValidationAsins,
    editedRowSet,
    selectedValidationService,
  } = useSelector((state: RootState) => state.asinValidationTableControl);
  return (
    <div className="button-container">
      <Button
        children="Cancel"
        onClick={(event) => {
          trackButtonClick("validationTable", "cancel", {});
          dispatch(setViewingValidationTable());
          // We aren't running any validations, so successfully exit.
          dispatch(validateAsinMarketplacesSuccess());
          dispatch(clearNotifications());
        }}
      />
      <Button
        children="Save"
        variant="primary"
        disabled={!edited}
        onClick={(e) => {
          trackButtonClick("validationTable", "save", {
            updatedAsinCount: Object.keys(editedRowSet).length,
          });
          dispatch(
            fetchValidateAsinMarketplaces(
              Object.keys(editedRowSet).map(
                (idx) => editableValidationAsins[+idx]
              ),
              selectedValidationService
            )
          );
          dispatch(setViewingValidationTable());
        }}
      />
    </div>
  );
};
